import { useState } from 'react'
import { useTheme, useMediaQuery, Box, Paper, AppBar, Toolbar, Typography, IconButton, Tooltip } from '@mui/material'
import { useHideSupportLauncher } from '@/components/elara/ElaraPanel'
import SettingsIcon from '@mui/icons-material/Settings'
import MenuIcon from '@mui/icons-material/Menu'
import AddIcon from '@mui/icons-material/Add'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'
import { ChatHistoryDrawer } from './ChatHistoryDrawer'
import { ChatInterface } from './ChatInterface'
import { ToolConfigurationPanel } from '@/pages/medconnect-agent/ui/ToolConfigurationPanel'
import { AIConsentDialog, hasAIConsent } from './AIConsentDialog'
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query'
import type { SerializedError } from '@reduxjs/toolkit'
import colors from '@/assets/scss/_themes-vars.module.scss'

const DRAWER_WIDTH = 280

export interface AgentChatLayoutProps {
  // Session data
  selectedSessionId: string | null | undefined
  sessions: Array<{ sessionId: string; title?: string }> | undefined
  isLoadingSessions: boolean
  sessionsError: FetchBaseQueryError | SerializedError | undefined

  // UI state
  title: string | undefined
  titleSuffix?: string
  drawerOpen: boolean
  isConfigPanelOpen: boolean

  // Event handlers
  onDrawerToggle: () => void
  onSessionSelect: (sessionId: string) => void
  onNewChat: () => void
  onSessionIdChange: (sessionId: string) => void
  onMessageSent: () => void
  onConfigPanelToggle: (open?: boolean) => void
}

/**
 * Shared layout component for agent chat pages.
 */
export function AgentChatLayout({
  selectedSessionId,
  sessions,
  isLoadingSessions,
  sessionsError,
  title,
  titleSuffix = '',
  drawerOpen,
  isConfigPanelOpen,
  onDrawerToggle,
  onSessionSelect,
  onNewChat,
  onSessionIdChange,
  onMessageSent,
  onConfigPanelToggle,
}: AgentChatLayoutProps) {
  const { t } = useTranslation()
  const theme = useTheme()
  const authUser = useAppSelector(selectAuthUser)
  const [consentGiven, setConsentGiven] = useState(hasAIConsent())
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  // On mobile the Freshworks "ASSISTANCE" pill sits right on top of the chat input — hide it here.
  useHideSupportLauncher(isMobile)

  return (
    <>
      <AIConsentDialog open={!consentGiven} onAccept={() => setConsentGiven(true)} />
      <Box
        sx={{
          display: 'flex',
          height: '100%',
          width: '100%',
          overflow: 'hidden',
          bgcolor: 'background.default',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          pt: {
            xs: theme.spacing(7),
            sm: theme.spacing(8),
          },
        }}
      >
        <ChatHistoryDrawer
          variant="drawer"
          sessions={sessions}
          isLoadingSessions={isLoadingSessions}
          sessionsError={sessionsError}
          selectedSessionId={selectedSessionId}
          onSessionSelect={onSessionSelect}
          onNewChat={onNewChat}
          drawerWidth={DRAWER_WIDTH}
          drawerOpen={drawerOpen}
          onDrawerClose={onDrawerToggle}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            width: '100%',
            overflow: 'hidden',
          }}
        >
          <Paper
            sx={{
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              borderRadius: 0,
              boxShadow: 'none',
              position: 'relative',
            }}
          >
            {/* Compact header — same style as ELARA side panel (46px, gray200 border) */}
            <AppBar position="static" color="transparent" elevation={0} sx={{ boxShadow: 'none', borderBottom: `1px solid ${colors.gray200}` }}>
              <Toolbar variant="dense" sx={{ minHeight: 46, px: 1.5, gap: 0.5 }}>
                <Tooltip title={t('page.medconnectAgent.chatHistory.title')}>
                  <IconButton size="small" aria-label="open drawer" onClick={onDrawerToggle} sx={{ color: colors.gray500, mr: 0.5 }}>
                    <MenuIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Typography variant="body2" noWrap sx={{ flexGrow: 1, textAlign: 'center', fontWeight: 500, color: colors.gray800, fontSize: '0.875rem' }}>
                  {title}
                  {titleSuffix}
                </Typography>
                <Tooltip title={t('page.medconnectAgent.chatHistory.newChat', { defaultValue: 'New conversation' })}>
                  <IconButton size="small" onClick={onNewChat} sx={{ color: colors.gray500 }}>
                    <AddIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('page.medconnectAgent.config.title')}>
                  <IconButton size="small" onClick={() => onConfigPanelToggle(true)} sx={{ color: colors.gray500 }}>
                    <SettingsIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
              </Toolbar>
            </AppBar>

            <Box
              sx={{
                flexGrow: 1,
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <ChatInterface sessionId={selectedSessionId} onSessionIdChange={onSessionIdChange} onMessageSent={onMessageSent} compact />
            </Box>
          </Paper>
        </Box>
        <ToolConfigurationPanel config={authUser?.medconnectAgentConfig || {}} open={isConfigPanelOpen} onClose={() => onConfigPanelToggle(false)} />
      </Box>
    </>
  )
}
